import { useState } from "react";
import { Clock, ShieldCheck, Megaphone, PiggyBank } from "lucide-react";

const fmt = (n) => `GHS ${Math.round(n).toLocaleString("en-GH")}`;

export default function Calculator() {
  const [orders, setOrders] = useState(800);
  const [avg, setAvg] = useState(60);
  const [branches, setBranches] = useState(1);

  const hoursSaved = (orders * branches * 8) / 60;
  const monthlyRevenue = orders * avg * branches;
  const recovered = monthlyRevenue * 0.015;
  const uplift = monthlyRevenue * 0.03;
  const yearly = (recovered + uplift) * 12;

  const results = [
    { icon: Clock, testid: "calc-hours-value", value: `${Math.round(hoursSaved).toLocaleString()} hrs`, label: "staff time saved every month (no paper tickets, no phone updates)" },
    { icon: ShieldCheck, testid: "calc-recovered-value", value: fmt(recovered), label: "recovered monthly from lost garments & disputes avoided" },
    { icon: Megaphone, testid: "calc-uplift-value", value: fmt(uplift), label: "extra monthly revenue from targeted promotions (Pro & Premium)" },
  ];

  return (
    <section id="calculator" data-testid="roi-calculator-section" className="bg-[#F8FAFC] py-16 sm:py-24">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <span className="inline-block text-xs font-bold uppercase tracking-widest text-[#1ABC9C] bg-[#E8F8F5] px-4 py-1.5 rounded-full mb-4">Savings Calculator</span>
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#2C3E50] max-w-2xl">
          What could WashFlow be worth to your business?
        </h2>
        <p className="mt-4 text-lg text-[#5D6D7E] leading-relaxed max-w-2xl">
          Move the sliders to match your business and see an instant estimate.
        </p>
        <div className="mt-10 grid lg:grid-cols-2 gap-8 items-start">
          <div className="bg-white rounded-2xl border border-[#E2E8F0] p-8 space-y-8">
            <div>
              <div className="flex items-center justify-between mb-3">
                <label htmlFor="calc-orders" className="text-base font-bold text-[#2C3E50]">Orders per month (per branch)</label>
                <span className="font-display font-extrabold text-[#1B4F72] text-lg" data-testid="calc-orders-display">{orders.toLocaleString()}</span>
              </div>
              <input id="calc-orders" data-testid="calc-orders-input" type="range" min="100" max="5000" step="50" value={orders} onChange={(e) => setOrders(Number(e.target.value))} className="w-full accent-[#1ABC9C] h-2" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-3">
                <label htmlFor="calc-avg" className="text-base font-bold text-[#2C3E50]">Average order value</label>
                <span className="font-display font-extrabold text-[#1B4F72] text-lg" data-testid="calc-avg-display">{fmt(avg)}</span>
              </div>
              <input id="calc-avg" data-testid="calc-avg-input" type="range" min="20" max="300" step="5" value={avg} onChange={(e) => setAvg(Number(e.target.value))} className="w-full accent-[#1ABC9C] h-2" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-3">
                <label htmlFor="calc-branches" className="text-base font-bold text-[#2C3E50]">Number of branches</label>
                <span className="font-display font-extrabold text-[#1B4F72] text-lg" data-testid="calc-branches-display">{branches}</span>
              </div>
              <input id="calc-branches" data-testid="calc-branches-input" type="range" min="1" max="10" step="1" value={branches} onChange={(e) => setBranches(Number(e.target.value))} className="w-full accent-[#1ABC9C] h-2" />
            </div>
            <p className="text-sm text-[#5D6D7E]">Estimates only, based on typical results from digitising laundry operations. Your actual numbers may be higher.</p>
          </div>
          <div className="space-y-4" data-testid="calc-results">
            {results.map((r, i) => (
              <div key={i} className="bg-white rounded-2xl border border-[#E2E8F0] p-6 flex items-center gap-5">
                <span className="w-12 h-12 rounded-xl bg-[#EBF5FB] flex items-center justify-center shrink-0">
                  <r.icon className="w-6 h-6 text-[#1B4F72]" />
                </span>
                <div>
                  <p className="font-display font-extrabold text-2xl text-[#1B4F72]" data-testid={r.testid}>{r.value}</p>
                  <p className="text-sm text-[#5D6D7E]">{r.label}</p>
                </div>
              </div>
            ))}
            <div className="bg-[#1B4F72] rounded-2xl p-6 flex items-center gap-5">
              <span className="w-12 h-12 rounded-xl bg-[#1ABC9C]/15 flex items-center justify-center shrink-0">
                <PiggyBank className="w-6 h-6 text-[#1ABC9C]" />
              </span>
              <div>
                <p className="font-display font-extrabold text-3xl text-white" data-testid="calc-yearly-value">{fmt(yearly)}</p>
                <p className="text-sm text-[#AED6F1]">estimated total value per year</p>
              </div>
            </div>
            <a href="#contact" data-testid="calc-cta-btn" className="block text-center bg-[#1ABC9C] hover:bg-[#16A085] text-white text-lg font-bold px-8 py-4 rounded-full transition-colors">
              Talk to OpsIQ about your numbers
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
